import { useSelector } from 'react-redux'
import { CommerceApiName } from '@typesApp/Api'
import { connectorEnvironmentSelector } from '@redux/slices/connectorSlice'
import { getEnvironmentOverride } from '@utils/getEnvironmentOverride'

interface UseEnvironmentResult {
  /**
   * Brand, e.g. cle
   */
  brand: string

  /**
   * Namespace used for this brand, typically the brand lowercase
   */
  translationNamespace: string

  /**
   * Country code, e.g. ca
   */
  country: string

  /**
   * Host app base path, e.g. localhost:3000/en-ca
   */
  basePath: string

  /**
   * Search parameters if applicable
   */
  basePathSearch?: string

  /**
   * Host lang id, e.g. en, fr
   */
  lang: string

  /**
   * Host app locale, e.g. en-ca, fr-ca, en-au, en-nz
   */
  locale: string

  /**
   * Host app store ID
   */
  storeId: string

  /**
   * Host app currency code, e.g. CAD
   */
  currency: string

  /**
   * Date format for parsing
   */
  dateFormat: string

  /**
   * Host app cart page path
   */
  cartPath: string

  /**
   * Host app sign in page path
   */
  signInPath: string

  /**
   * Host app sign in page redirect parameter, e.g. redirectRoute
   */
  signInRedirectParam: string

  /**
   * Strip locale code from sign in redirect url, e.g. remove the /fr-fr/ portion
   */
  signInRedirectStripLocale: boolean

  /**
   * ecommerce system code
   */
  ecommerceCode: string | null

  /**
   * ecommerce host url
   */
  ecommerceTransactionHost: string

  /**
   * Host app terms and conditions page
   */
  termsConditionsUrl: string

  /**
   * Host app privacy policy page
   */
  privacyPolicyUrl: string

  /**
   * Host app cookies policy page
   */
  cookiesPolicyUrl: string

  /**
   * Host app faq page
   */
  faqUrl: string

  /**
   * Host app landing page for direct billing
   */
  insuranceLandingPageUrl: string
}

/**
 * Hook to abstract host app / environment related details
 */
export const useEnvironment = (): UseEnvironmentResult => {
  const environment = useSelector(connectorEnvironmentSelector)
  const {
    brand,
    country,
    storeId,
    locale,
    currency,
    dateFormat,
    basePath,
    basePathSearch,
    cartPath,
    signInPath,
    signInRedirectParam,
    signInRedirectStripLocale,
    ecommerceCode,
    ecommerceTransactionHost = '',
    termsConditionsUrl,
    cookiesPolicyUrl,
    privacyPolicyUrl,
    faqUrl,
    insuranceLandingPageUrl,
  } = environment

  const override = getEnvironmentOverride()

  const replaceLocale = (value: string | undefined): string | undefined => {
    return value?.replace('{locale}', locale)
  }

  return {
    brand,
    translationNamespace: brand,
    country,
    basePath,
    basePathSearch,
    locale,
    lang: override?.lang ?? locale?.split('-')[0],
    storeId,
    currency,
    dateFormat: dateFormat || 'MM/dd/yyyy',
    cartPath,
    signInPath,
    signInRedirectParam: signInRedirectParam || 'redirectUrl',
    signInRedirectStripLocale: signInRedirectStripLocale || false,
    ecommerceCode,
    ecommerceTransactionHost,
    termsConditionsUrl: replaceLocale(override?.termsConditionsUrl) ?? termsConditionsUrl,
    privacyPolicyUrl: replaceLocale(override?.privacyPolicyUrl) ?? privacyPolicyUrl,
    cookiesPolicyUrl: replaceLocale(override?.cookiesPolicyUrl) ?? cookiesPolicyUrl,
    faqUrl: replaceLocale(override?.faqUrl) ?? faqUrl,
    insuranceLandingPageUrl: replaceLocale(override?.insuranceLandingPageUrl) ?? insuranceLandingPageUrl,
  }
}
